import { createRouter, createWebHistory } from "vue-router";
import { computed } from "vue";
import { ls } from "@/plugin/secure";
import { userStore } from "@/store/UserStore";
import menus from "./menus.json";
import i18n from "../plugin/i18n";

const routes = [
  {
    path: "/",
    name: "login",
    component: () => import("@/view/Login.vue"),
    meta: {},
  },
  {
    path: "/signUp",
    name: "signUp",
    component: () => import("@/view/SignUp.vue"),
    meta: {},
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.onError((error) => {
  const message = String(error?.message ?? "");
  const isChunkLoadError =
    /ChunkLoadError/i.test(message) ||
    /Loading CSS chunk .* failed/i.test(message) ||
    /Loading chunk .* failed/i.test(message);

  if (!isChunkLoadError || typeof window === "undefined") {
    return;
  }

  const reloadKey = "miri_chunk_reload_once";
  const isReloaded = window.sessionStorage.getItem(reloadKey) === "1";

  if (!isReloaded) {
    window.sessionStorage.setItem(reloadKey, "1");
    window.location.reload();
  } else {
    window.sessionStorage.removeItem(reloadKey);
  }
});

function filteringMenus(defaultMenu, userMenu) {
  function filterMenu(menu) {
    const isMenuInUserMenu = userMenu.some(
      (item) => item.vueFileUrl === menu.vueFileUrl
    );
    if (isMenuInUserMenu) {
      menu.component = () => import(`@/${menu.vueFileUrl.replace("_copy", "")}`);
      menu.meta.breadcrumb = computed(() =>
        i18n.global.t(`MENU_${menu.name.toUpperCase()}`)
      );
      const target = userMenu.find(
        (item) => item.vueFileUrl === menu.vueFileUrl
      );
      menu.meta.methods = target.methods;
    }
    if (menu.children) {
      menu.meta.breadcrumb = computed(() =>
        i18n.global.t(`MENU_${menu.name.toUpperCase()}`)
      );
      menu.children = menu.children.filter(filterMenu);
    } else {
      if (menu.vueFileUrl) {
        return isMenuInUserMenu;
      }
    }
    return (
      !menu.vueFileUrl ||
      isMenuInUserMenu ||
      (menu.children && menu.children.length > 0)
    );
  }

  return defaultMenu.filter(filterMenu);
}

function normalizeMenusForRouter(menuList) {
  const usedNames = new Set();
  const usedPaths = new Set();

  const joinPath = (parentPath, currentPath) => {
    if (!currentPath) {
      return parentPath || "/";
    }
    if (currentPath.startsWith("/")) {
      return currentPath;
    }
    if (!parentPath || parentPath === "/") {
      return `/${currentPath}`;
    }
    return `${parentPath.replace(/\/$/, "")}/${currentPath.replace(/^\//, "")}`;
  };

  const walk = (menus, parentPath = "") => {
    const normalized = [];
    const source = Array.isArray(menus) ? menus : [];

    source.forEach((menu) => {
      if (!menu || typeof menu !== "object") {
        return;
      }

      const fullPath = joinPath(parentPath, menu.path);
      const name = String(menu.name ?? "").trim();

      if (name && usedNames.has(name)) {
        return;
      }
      if (fullPath && usedPaths.has(fullPath)) {
        return;
      }

      if (name) {
        usedNames.add(name);
      }
      if (fullPath) {
        usedPaths.add(fullPath);
      }

      const copied = {
        ...menu,
        meta: menu.meta ? { ...menu.meta } : {},
      };

      if (Array.isArray(menu.children) && menu.children.length > 0) {
        copied.children = walk(menu.children, fullPath);
      }

      normalized.push(copied);
    });

    return normalized;
  };

  return walk(menuList);
}

let once = false;
router.beforeEach((to) => {
  const useUserStore = userStore();

  // [DEMO MODE] 모든 메뉴를 로드하도록 설정
  if (once == false) {
    try {
      const allMenus = normalizeMenusForRouter(
        JSON.parse(JSON.stringify(menus))
      );
      const prepareMenu = (mList) => {
        mList.forEach((menu) => {
          if (menu.vueFileUrl) {
            menu.component = () =>
              import(`@/${menu.vueFileUrl.replace("_copy", "")}`);
            menu.meta = menu.meta || {};
            menu.meta.breadcrumb = computed(() =>
              i18n.global.t(`MENU_${menu.name.toUpperCase()}`)
            );
            menu.meta.methods = [];
          }
          if (menu.children) {
            menu.meta = menu.meta || {};
            menu.meta.breadcrumb = computed(() =>
              i18n.global.t(`MENU_${menu.name.toUpperCase()}`)
            );
            prepareMenu(menu.children);
          }
        });
      };
      prepareMenu(allMenus);

      allMenus.forEach((route) => {
        router.addRoute(route);
      });
      useUserStore.setMenus(allMenus);
    } catch (e) {
      console.error("Menu initialization error:", e);
      ls.clear();
      return { path: "/" };
    }
    once = true;
  }

  const isPublicRoute = to.name === "login" || to.name === "signUp";

  if (!isPublicRoute && !ls.get("accessToken")) {
    return { path: "/" };
  }

  return true;
});
export default router;
